import React, { useCallback, useEffect, useMemo, useState } from "react";
import { marked } from "marked";
import { getDoc, onSnapshot } from "firebase/firestore";
import { useParams, useNavigate } from "react-router-dom";

import {
  approvePost,
  regeneratePost,
  deletePost,
  editImageFn,
  regenImageFn,
  pendingRef,
} from "./blogFirebase";
import { useBlogTask } from "./useBlogTask";
import ConfirmModal  from "./ConfirmModal";
import BlogActionBar from "./BlogActionBar";
import BlogDiffPanel from "./BlogDiffPanel";
import BlogPostBody  from "./BlogPostBody";

// ── Marked config ─────────────────────────────────────────────────────────────
marked.use({ mangle: false, headerIds: false });

// ── AdminBlogPost ─────────────────────────────────────────────────────────────
const AdminBlogPost = () => {
  const { docId } = useParams();
  const navigate  = useNavigate();

  const [post, setPost]         = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [busy, setBusy]         = useState(false);
  const [actionMsg, setActionMsg] = useState("");
  const [confirmModal, setConfirmModal] = useState(null);

  // Edit panel state
  const [showEdit, setShowEdit]           = useState(false);
  const [showRegenForm, setShowRegenForm] = useState(false);
  const [regenFeedback, setRegenFeedback] = useState("");

  // Main image state
  const [showImgForm, setShowImgForm]         = useState(false);
  const [imgFormMode, setImgFormMode]         = useState("edit");
  const [editPrompt, setEditPrompt]           = useState("");
  const [newImgPrompt, setNewImgPrompt]       = useState("");
  const [newImgUseDefault, setNewImgUseDefault] = useState(true);
  const [showImgPrompt, setShowImgPrompt]     = useState(false);

  // Secondary image state
  const [showSecondaryImgForm, setShowSecondaryImgForm]             = useState(false);
  const [secondaryImgFormMode, setSecondaryImgFormMode]             = useState("edit");
  const [secondaryEditPrompt, setSecondaryEditPrompt]               = useState("");
  const [secondaryNewImgPrompt, setSecondaryNewImgPrompt]           = useState("");
  const [secondaryNewImgUseDefault, setSecondaryNewImgUseDefault]   = useState(true);
  const [showSecondaryImgPrompt, setShowSecondaryImgPrompt]         = useState(false);

  // Version diff state
  const [showDiff, setShowDiff]       = useState(false);
  const [priorPost, setPriorPost]     = useState(null);
  const [diffLoading, setDiffLoading] = useState(false);

  // Live task progress (Firestore WebSocket listener)
  const { taskMsg, startTask, stopTask } = useBlogTask();

  // Reset busy when navigating to a different doc
  useEffect(() => { setBusy(false); }, [docId]);

  // Subscribe to this specific pending doc
  useEffect(() => {
    const ref  = pendingRef(docId);
    const unsub = onSnapshot(ref, (snap) => {
      if (!snap.exists()) {
        setNotFound(true);
        setPost(null);
      } else {
        setPost({ id: snap.id, ...snap.data() });
        setNotFound(false);
      }
    }, (err) => {
      console.error("BlogPost snapshot error:", err);
      setNotFound(true);
    });
    return () => unsub();
  }, [docId]);

  const setMsg = (msg) => {
    setActionMsg(msg);
    setTimeout(() => setActionMsg(""), 5000);
  };

  const contentHtml = useMemo(
    () => marked.parse(post?.content || ""),
    [post?.content]
  );

  // ── Diff ──────────────────────────────────────────────────────────────────
  const handleToggleDiff = useCallback(async () => {
    if (priorPost) { setShowDiff(v => !v); return; }
    setDiffLoading(true);
    try {
      const snap = await getDoc(pendingRef(post.replacedPrior));
      if (snap.exists()) setPriorPost({ id: snap.id, ...snap.data() });
    } catch (e) {
      console.error("Failed to load prior version:", e);
    } finally {
      setDiffLoading(false);
      setShowDiff(true);
    }
  }, [priorPost, post?.replacedPrior]);

  // ── Confirm modal helpers ─────────────────────────────────────────────────
  const openConfirm  = (opts) => setConfirmModal(opts);
  const closeConfirm = () => setConfirmModal(null);

  // ── Action handlers ───────────────────────────────────────────────────────
  const handleApprove = () => {
    openConfirm({
      title: "Approve & Publish",
      message: `"${post.title}" will be published to the live blog.`,
      confirmLabel: "✅ Publish",
      confirmClass: "bg-green-600 hover:bg-green-700",
      onConfirm: doApprove,
    });
  };

  const doApprove = async () => {
    closeConfirm();
    setActionMsg("✅ Publishing post…");
    setBusy(true);
    try {
      await approvePost({ docId });
      navigate("/blog");
    } catch (e) {
      setMsg("❌ " + e.message);
      setBusy(false);
    }
  };

  const handleDelete = () => {
    openConfirm({
      title: "Delete Post",
      message: `"${post.title}" will be permanently deleted. This cannot be undone.`,
      confirmLabel: "🗑 Delete",
      confirmClass: "bg-red-600 hover:bg-red-700",
      onConfirm: doDelete,
    });
  };

  const doDelete = async () => {
    closeConfirm();
    setBusy(true);
    try {
      await deletePost({ docId });
      navigate("/blog");
    } catch (e) {
      setMsg("❌ " + e.message);
      setBusy(false);
    }
  };

  const handleRegenerate = async () => {
    setActionMsg("✏️ Rewriting post with AI…");
    setBusy(true);
    setShowRegenForm(false);
    setShowEdit(false);
    const taskId = startTask();
    try {
      const result = await regeneratePost({ docId, feedbackNote: regenFeedback.trim(), taskId });
      setRegenFeedback("");
      navigate(`/blog/${result.data.newDocId}`);
    } catch (e) {
      setMsg("❌ " + e.message);
      setBusy(false);
    } finally {
      stopTask();
    }
  };

  const handleFullRegenerate = () => {
    openConfirm({
      title: "Regenerate from Scratch",
      message: "A completely new post will be generated using fresh AI analysis. The current draft will be discarded.",
      confirmLabel: "♻️ Regenerate",
      confirmClass: "bg-orange-600 hover:bg-orange-700",
      onConfirm: doFullRegenerate,
    });
  };

  const doFullRegenerate = async () => {
    closeConfirm();
    setActionMsg("♻️ Regenerating from scratch…");
    setBusy(true);
    setShowEdit(false);
    const taskId = startTask();
    try {
      const result = await regeneratePost({ docId, fullRegenerate: true, taskId });
      navigate(`/blog/${result.data.newDocId}`);
    } catch (e) {
      setMsg("❌ " + e.message);
      setBusy(false);
    } finally {
      stopTask();
    }
  };

  // ── Main image handlers ───────────────────────────────────────────────────
  const handleEditImage = async () => {
    if (!editPrompt.trim()) return;
    setActionMsg("✏️ Editing hero image…");
    setBusy(true);
    setShowEdit(false);
    const taskId = startTask();
    try {
      await editImageFn({ docId, editPrompt: editPrompt.trim(), imageTarget: "main", taskId });
      setMsg("🎨 Hero image updated!");
      setEditPrompt("");
    } catch (e) {
      setMsg("❌ " + e.message);
    } finally { stopTask(); setBusy(false); }
  };

  const handleNewImage = async () => {
    setActionMsg("🖼 Generating new hero image…");
    setBusy(true);
    setShowEdit(false);
    const taskId = startTask();
    try {
      await regenImageFn({
        docId,
        customPrompt: newImgPrompt.trim() || undefined,
        imageTarget: "main",
        taskId,
        useDefaultPrompt: newImgUseDefault,
      });
      setMsg("🖼 New hero image ready!");
      setNewImgPrompt("");
    } catch (e) {
      setMsg("❌ " + e.message);
    } finally { stopTask(); setBusy(false); }
  };

  // ── Secondary image handlers ──────────────────────────────────────────────
  const handleEditSecondaryImage = async () => {
    if (!secondaryEditPrompt.trim()) return;
    setActionMsg("✏️ Editing mid-article image…");
    setBusy(true);
    setShowEdit(false);
    const taskId = startTask();
    try {
      await editImageFn({ docId, editPrompt: secondaryEditPrompt.trim(), imageTarget: "secondary", taskId });
      setMsg("🎨 Mid-article image updated!");
      setSecondaryEditPrompt("");
    } catch (e) {
      setMsg("❌ " + e.message);
    } finally { stopTask(); setBusy(false); }
  };

  const handleNewSecondaryImage = async () => {
    setActionMsg("🖼 Generating new mid-article image…");
    setBusy(true);
    setShowEdit(false);
    const taskId = startTask();
    try {
      await regenImageFn({
        docId,
        customPrompt: secondaryNewImgPrompt.trim() || undefined,
        imageTarget: "secondary",
        taskId,
        useDefaultPrompt: secondaryNewImgUseDefault,
      });
      setMsg("🖼 New mid-article image ready!");
      setSecondaryNewImgPrompt("");
    } catch (e) {
      setMsg("❌ " + e.message);
    } finally { stopTask(); setBusy(false); }
  };

  // ── Render states ─────────────────────────────────────────────────────────
  if (!post && !notFound) {
    return (
      <div className="flex justify-center items-center py-16">
        <div className="text-xl text-gray-500">Loading post…</div>
      </div>
    );
  }

  if (notFound) {
    return (
      <div className="text-center py-16 bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="text-5xl mb-4">🔍</div>
        <p className="text-gray-500 text-lg">Post not found or already published.</p>
        <button
          onClick={() => navigate("/blog")}
          className="mt-4 px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 text-sm"
        >
          ← Back to list
        </button>
      </div>
    );
  }

  // ── Main render ───────────────────────────────────────────────────────────
  return (
    <div className="relative min-h-screen bg-gray-50 -mx-4 -mt-6">
      {busy && (
        <div className="absolute inset-0 z-50 flex flex-col items-center justify-start pt-6 bg-white/60 backdrop-blur-sm rounded-lg">
          <div className="text-center px-6">
            <div className="w-14 h-14 border-4 border-purple-200 border-t-purple-600 rounded-full animate-spin mx-auto mb-5" />
            <p className="text-gray-700 text-base font-semibold mb-2">
              {actionMsg || "Working…"}
            </p>
            {taskMsg && (
              <p className="text-gray-600 text-sm mb-1">{taskMsg}</p>
            )}
            <p className="text-gray-500 text-sm">This may take a minute — please don't close this page.</p>
          </div>
        </div>
      )}
      {confirmModal && (
        <ConfirmModal
          title={confirmModal.title}
          message={confirmModal.message}
          confirmLabel={confirmModal.confirmLabel}
          confirmClass={confirmModal.confirmClass}
          onConfirm={confirmModal.onConfirm}
          onCancel={closeConfirm}
        />
      )}

      <BlogActionBar
        onBack={() => navigate("/blog")}
        actionMsg={actionMsg}
        hasPrior={!!post.replacedPrior}
        diffLoading={diffLoading}
        showDiff={showDiff}
        onToggleDiff={handleToggleDiff}
        showEdit={showEdit}
        onToggleEdit={() => { setShowEdit(v => !v); setShowRegenForm(false); }}
        busy={busy}
        onApprove={handleApprove}
        showRegenForm={showRegenForm}
        setShowRegenForm={setShowRegenForm}
        regenFeedback={regenFeedback}
        setRegenFeedback={setRegenFeedback}
        onRegenerate={handleRegenerate}
        onFullRegenerate={handleFullRegenerate}
        showImgForm={showImgForm}
        setShowImgForm={setShowImgForm}
        imgFormMode={imgFormMode}
        setImgFormMode={setImgFormMode}
        editPrompt={editPrompt}
        setEditPrompt={setEditPrompt}
        newImgPrompt={newImgPrompt}
        setNewImgPrompt={setNewImgPrompt}
        newImgUseDefault={newImgUseDefault}
        setNewImgUseDefault={setNewImgUseDefault}
        onEditImage={handleEditImage}
        onNewImage={handleNewImage}
        showSecondaryImgForm={showSecondaryImgForm}
        setShowSecondaryImgForm={setShowSecondaryImgForm}
        secondaryImgFormMode={secondaryImgFormMode}
        setSecondaryImgFormMode={setSecondaryImgFormMode}
        secondaryEditPrompt={secondaryEditPrompt}
        setSecondaryEditPrompt={setSecondaryEditPrompt}
        secondaryNewImgPrompt={secondaryNewImgPrompt}
        setSecondaryNewImgPrompt={setSecondaryNewImgPrompt}
        secondaryNewImgUseDefault={secondaryNewImgUseDefault}
        setSecondaryNewImgUseDefault={setSecondaryNewImgUseDefault}
        onEditSecondaryImage={handleEditSecondaryImage}
        onNewSecondaryImage={handleNewSecondaryImage}
        onDelete={handleDelete}
      />

      {showDiff && priorPost && (
        <BlogDiffPanel priorPost={priorPost} post={post} />
      )}

      <BlogPostBody
        post={post}
        contentHtml={contentHtml}
        showImgPrompt={showImgPrompt}
        onToggleImgPrompt={() => setShowImgPrompt(v => !v)}
        showSecondaryImgPrompt={showSecondaryImgPrompt}
        onToggleSecondaryImgPrompt={() => setShowSecondaryImgPrompt(v => !v)}
      />
    </div>
  );
};

export default AdminBlogPost;
